import React, { useState } from 'react'
import { Icon, Popover } from 'antd'
import axios from 'util/Api'

/**
 * AiScore — an "AI 73%" score that opens "what the AI used to decide" on click:
 * the found PV item and the register asset side by side (type, sub-type, model,
 * location, sub-location - the register's as on the PV date - and the photo
 * description), and the probability. Nothing else (no request JSON, no cost).
 *
 * The details come either from a comparison already in memory (`state`, from the
 * link picker's search) or are fetched once on first click by comparison id
 * (GET /pv-photo-description/comparison/:id) for a saved AI decision.
 */

const ROWS = [
  ['Asset type', 'asset_type', 'asset_type'],
  ['Sub-type', 'sub_type', 'sub_type'],
  ['Model', 'model', 'model'],
  ['Location', 'location', 'location_on_pv_date'],
  ['Sub-location', 'sub_location', 'sub_location_on_pv_date'],
]

export const pct = (p) => `${Math.round(p * 100)}%`
export const band = (p, threshold = 0.5) => p >= 0.8 ? 'hi' : p >= threshold ? 'mid' : 'lo'

const val = (v) => (v === null || v === undefined) ? <em style={{ color: '#8695a7' }}>not recorded</em> : v
const photos = (list) => (list && list.length)
  ? list.map((t, i) => <div key={i}>{list.length > 1 ? `${i + 1}. ` : ''}{t}</div>)
  : <em style={{ color: '#8695a7' }}>—</em>

export const AiBasisView = ({ pv, asset, assetCode, prob }) => (
  <div style={{ maxWidth: 560, fontSize: 12 }}>
    <div style={{ marginBottom: 6 }}>
      AI: <b>{typeof prob === 'number' ? pct(prob) : '—'}</b> probability that the found item is <b>{assetCode || 'this asset'}</b>
    </div>
    <table style={{ borderCollapse: 'collapse', width: '100%' }}>
      <thead>
        <tr>
          <th style={th} /><th style={th}>Found item (PV)</th><th style={th}>Register asset{assetCode ? ` ${assetCode}` : ''} · on PV date</th>
        </tr>
      </thead>
      <tbody>
        {ROWS.map(([label, a, b]) => (
          <tr key={label}><td style={tdl}>{label}</td><td style={td}>{val(pv && pv[a])}</td><td style={td}>{val(asset && asset[b])}</td></tr>
        ))}
        <tr><td style={tdl}>Photo (colour / material)</td>
          <td style={td}>{photos(pv && pv.photo_descriptions)}</td><td style={td}>{photos(asset && asset.photo_descriptions)}</td></tr>
      </tbody>
    </table>
  </div>
)

const th = { textAlign: 'left', fontSize: 10.5, color: '#8695a7', padding: '3px 6px', borderBottom: '1px solid #e0e6ee' }
const td = { padding: '3px 6px', borderBottom: '1px solid #eef2f7', verticalAlign: 'top' }
const tdl = { ...td, color: '#5b6b7f', whiteSpace: 'nowrap' }

// The score, clickable. Pass `state` (request.state in memory) or `comparisonId` (fetched on click).
export const AiScore = ({ prob, assetCode, state, comparisonId, threshold, className = '', label = 'AI' }) => {
  const [loaded, setLoaded] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const onOpen = (open) => {
    if (!open || state || loaded || loading || !comparisonId) return
    setLoading(true); setError(null)
    axios.get(`/pv-photo-description/comparison/${comparisonId}`)
      .then(res => setLoaded(res.data))
      .catch(err => setError((err && err.response && err.response.data && err.response.data.message) || (err && err.message) || 'request failed'))
      .then(() => setLoading(false))
  }
  const content = state
    ? <AiBasisView pv={state.found_item_pv} asset={state.register_asset} assetCode={assetCode} prob={prob} />
    : loaded
      ? <AiBasisView pv={loaded.pv} asset={loaded.asset} assetCode={loaded.assetCode || assetCode} prob={loaded.sameProbability} />
      : error ? <span style={{ color: '#f5222d' }}>Could not load: {error}</span>
        : <span><Icon type="loading" /> Loading…</span>
  return (
    <Popover content={content} trigger="click" placement="left" onVisibleChange={onOpen}>
      <span className={'ai-score ' + band(prob, threshold) + ' ' + className}
        onClick={(e) => e.stopPropagation()} title="What the AI used to decide">
        {label} <b className="tnum">{pct(prob)}</b>
      </span>
    </Popover>
  )
}
